/**
 * useBuildReviewActions — shared handlers for opening the IntakeReviewWorkspace
 * in Push Mode from any surface (WhatNeedsAttentionStrip, OperationsCommandCenter,
 * Job Detail, etc.). Mirrors the logic in BuildReviewPanel so the gate can be
 * cleared from anywhere a "Complete Project Review" CTA appears, without
 * navigating the user away from their current context.
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import type { Job } from "@/data/types";
import type { BuildReviewEvent, RevisionReason } from "@/data/buildReviewTypes";
import { REVISION_REASON_LABELS } from "@/data/buildReviewTypes";

export function useBuildReviewActions() {
  const { jobs, setJobs, setBuildReviewEvents } = useAppData();
  const [reviewJob, setReviewJob] = useState<Job | null>(null);

  const openReview = useCallback((job: Job) => setReviewJob(job), []);
  const closeReview = useCallback(() => setReviewJob(null), []);

  const addEvent = useCallback(
    (jobId: string, action: BuildReviewEvent["action"], extra?: Partial<BuildReviewEvent>) => {
      const evt: BuildReviewEvent = {
        id: crypto.randomUUID(),
        jobId,
        action,
        timestamp: new Date().toISOString(),
        performedBy: "ops_manager",
        ...extra,
      };
      setBuildReviewEvents((prev) => [...prev, evt]);
    },
    [setBuildReviewEvents],
  );

  /** Internal: mutate job state for an approval. Does NOT close the workspace. */
  const commitApproval = useCallback(
    (jobId: string) => {
      addEvent(jobId, "approved");
      setJobs((prev) =>
        prev.map((j) =>
          j.id === jobId
            ? {
                ...j,
                scopeLocked: true,
                intakeApproved: true,
                approvedForOrdering: true,
                planningStatus: "In Planning" as const,
                currentStage: "Review Complete" as const,
                activityHistory: [
                  ...j.activityHistory,
                  {
                    id: `act-${Date.now()}`,
                    date: new Date().toISOString(),
                    action: "Build review approved — moved to Planning",
                    user: "Ops Manager",
                    noteType: "ops" as const,
                  },
                ],
              }
            : j,
        ),
      );
      toast.success("Build review approved", { description: "Job moved to Material Planning" });
    },
    [addEvent, setJobs],
  );

  /** Legacy: commit approval AND close the workspace (used by surfaces without
   *  Push-Mode chaining). */
  const onApprove = useCallback(
    (jobId: string) => {
      commitApproval(jobId);
      setReviewJob(null);
    },
    [commitApproval],
  );

  const onRequestRevision = useCallback(
    (jobId: string, notes: string, materials: string, reason: RevisionReason) => {
      addEvent(jobId, "revision_requested", {
        revisionNotes: notes,
        revisionReason: reason,
        materialChanges: materials,
      });
      const job = jobs.find((j) => j.id === jobId);
      setJobs((prev) =>
        prev.map((j) =>
          j.id === jobId
            ? { ...j, holdStatus: "On Hold — Sales Action Required", holdReason: REVISION_REASON_LABELS[reason] }
            : j,
        ),
      );
      toast.info("Revision requested", {
        description: `On Hold — assigned to ${job?.salesRep || "Sales Rep"} for resolution`,
      });
      setReviewJob(null);
    },
    [addEvent, jobs, setJobs],
  );

  const onSendToSales = useCallback(
    (jobId: string) => {
      addEvent(jobId, "sent_to_sales");
      toast.info("Sent to Sales", { description: "Awaiting sales confirmation" });
      setReviewJob(null);
    },
    [addEvent],
  );

  const onSalesApprove = useCallback(
    (jobId: string) => {
      addEvent(jobId, "sales_confirmed");
      addEvent(jobId, "change_order_created", { notes: "Change order generated from scope revision" });
      toast.success("Sales approved", { description: "Change order created — awaiting customer signature" });
      setReviewJob(null);
    },
    [addEvent],
  );

  const onCustomerSigned = useCallback(
    (jobId: string) => {
      addEvent(jobId, "customer_signed");
      toast.success("Customer signed", { description: "Moved to Final Build Review" });
      setReviewJob(null);
    },
    [addEvent],
  );

  const onFinalApproval = useCallback(
    (jobId: string) => {
      addEvent(jobId, "final_approved");
      addEvent(jobId, "approved");
      toast.success("Final review approved", { description: "Job cleared for Material Planning" });
      setReviewJob(null);
    },
    [addEvent],
  );

  /** Commit final approval mutation only — does NOT close the workspace. */
  const commitFinalApproval = useCallback(
    (jobId: string) => {
      addEvent(jobId, "final_approved");
      addEvent(jobId, "approved");
      // Mirror commitApproval state mutation so downstream gates see approval.
      setJobs((prev) =>
        prev.map((j) =>
          j.id === jobId
            ? {
                ...j,
                scopeLocked: true,
                intakeApproved: true,
                approvedForOrdering: true,
                planningStatus: "In Planning" as const,
                currentStage: "Review Complete" as const,
              }
            : j,
        ),
      );
      toast.success("Final review approved", { description: "Job cleared for Material Planning" });
    },
    [addEvent, setJobs],
  );

  return {
    reviewJob,
    openReview,
    closeReview,
    onApprove,
    commitApproval,
    commitFinalApproval,
    onRequestRevision,
    onSendToSales,
    onSalesApprove,
    onCustomerSigned,
    onFinalApproval,
  };
}
